import { useEffect, useRef, type RefObject } from 'react';
import type L from 'leaflet';
import type { Feature } from 'geojson';
import { useServiceAreasStore, POLYGON_RENDERING_ENABLED } from '../../store/serviceAreasStore';
import { getLeaflet, LABEL_HIDE_POLYGON_ZOOM } from './leafletHelpers';
import { useTheme } from '../../theme';

/**
 * Render / diff service-area polygon outlines whenever the cached polygon
 * set changes. One `L.GeoJSON` layer per area id lives inside the supplied
 * overlay layer group so polygons stay independent of the marker cluster
 * layer and can be toggled en masse via pane display.
 */
export function useServiceAreaPolygons({
  mapRef,
  overlayLayerRef,
  isLoaded,
}: {
  mapRef: RefObject<L.Map | null>;
  overlayLayerRef: RefObject<L.LayerGroup | null>;
  isLoaded: boolean;
}): void {
  const polygonLayersRef = useRef<Map<string, L.GeoJSON>>(new Map());
  const polygonsByAreaId = useServiceAreasStore((s) => s.polygonsByAreaId);
  const { colors } = useTheme();

  useEffect(() => {
    if (!POLYGON_RENDERING_ENABLED) return;
    const overlay = overlayLayerRef.current;
    const map = mapRef.current;
    if (!overlay || !map || !isLoaded) return;
    const L = getLeaflet();

    const render = () => {
      const zoom = map.getZoom();
      const shouldShow = zoom < LABEL_HIDE_POLYGON_ZOOM;
      const nextIds = new Set(Object.keys(polygonsByAreaId));
      for (const [id, layer] of polygonLayersRef.current) {
        if (!nextIds.has(id)) {
          overlay.removeLayer(layer);
          polygonLayersRef.current.delete(id);
        }
      }
      for (const id of nextIds) {
        const feature = polygonsByAreaId[id];
        let layer = polygonLayersRef.current.get(id);
        if (!layer) {
          layer = L.geoJSON(feature as Feature, {
            // Cached layers keep their creation-time stroke; acceptable while
            // POLYGON_RENDERING_ENABLED stays off (theme flips won't restyle).
            style: {
              color: colors.accent,
              weight: 2,
              fillOpacity: 0,
              interactive: false,
            },
          });
          layer.addTo(overlay);
          polygonLayersRef.current.set(id, layer);
        }
        const element = (
          layer as unknown as { getPane?: () => HTMLElement | undefined }
        ).getPane?.();
        if (element) {
          element.style.display = shouldShow ? '' : 'none';
        }
      }
    };

    render();
    map.on('zoomend', render);
    return () => {
      map.off('zoomend', render);
    };
  }, [polygonsByAreaId, isLoaded, mapRef, overlayLayerRef, colors.accent]);
}
